import { useState, useEffect, useCallback, useRef } from 'react';
import { apiRequest } from '../config/api';
import { useDataCache } from '../context/DataCacheContext';

/**
 * Hook for fetching and managing media list
 */
export function useMediaList(options = {}) {
  const { type = 'all', search = '', sort = 'created_at', order = 'desc', limit = 50 } = options;

  const { getCachedData, setCachedData } = useDataCache();
  const cached = getCachedData('media');
  const [media, setMedia] = useState(cached || []);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(!cached);
  const skipNextLoadingRef = useRef(!!cached);
  const [error, setError] = useState(null);
  const [offset, setOffset] = useState(0);

  const fetchMedia = useCallback(async (newOffset = 0) => {
    if (!skipNextLoadingRef.current) {
      setLoading(true);
    }
    setError(null);

    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: newOffset.toString(),
        sort,
        order,
      });

      if (type && type !== 'all') params.append('type', type);
      if (search) params.append('search', search);

      const response = await apiRequest(`/media?${params}`);

      if (!response.ok) {
        throw new Error('Failed to fetch media');
      }

      const data = await response.json();
      setMedia(data.items);
      setTotal(data.total);
      setCachedData('media', data.items);
      setOffset(newOffset);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      skipNextLoadingRef.current = false;
    }
  }, [type, search, sort, order, limit, setCachedData]);

  useEffect(() => {
    fetchMedia(0);
  }, [fetchMedia]);

  const loadMore = () => {
    if (offset + limit < total) {
      fetchMedia(offset + limit);
    }
  };

  return { media, total, loading, error, refetch: () => fetchMedia(0), loadMore, hasMore: offset + limit < total };
}

/**
 * Hook for uploading media
 */
export function useMediaUpload() {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  const uploadMedia = async (file, onSuccess) => {
    setUploading(true);
    setProgress(0);
    setError(null);

    try {
      // Get file extension
      const extension = file.name.split('.').pop().toLowerCase();

      // Get image dimensions if it's an image
      let width = null;
      let height = null;

      if (file.type.startsWith('image/')) {
        const dimensions = await getImageDimensions(file);
        width = dimensions.width;
        height = dimensions.height;
      }

      // Step 1: Get signed upload URL
      setProgress(10);
      const uploadUrlResponse = await apiRequest('/media/upload-url', {
        method: 'POST',
        body: JSON.stringify({
          file_extension: extension,
          file_type: file.type,
          file_size: file.size,
          width,
          height,
        }),
      });

      if (!uploadUrlResponse.ok) {
        const errorData = await uploadUrlResponse.json();
        throw new Error(errorData.detail || 'Failed to get upload URL');
      }

      const { upload_url, file_path, public_url } = await uploadUrlResponse.json();

      // Step 2: Upload to Supabase
      setProgress(30);
      const supabaseResponse = await fetch(upload_url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!supabaseResponse.ok) {
        throw new Error('Failed to upload file to storage');
      }

      setProgress(70);

      // Step 3: Create media record
      const createResponse = await apiRequest('/media', {
        method: 'POST',
        body: JSON.stringify({
          filename: file.name,
          original_filename: file.name,
          storage_path: file_path,
          public_url,
          file_type: file.type,
          file_size: file.size,
          width,
          height,
        }),
      });

      if (!createResponse.ok) {
        throw new Error('Failed to create media record');
      }

      setProgress(100);
      const mediaRecord = await createResponse.json();

      if (onSuccess) {
        onSuccess(mediaRecord);
      }

      return mediaRecord;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setUploading(false);
    }
  };

  return { uploadMedia, uploading, progress, error };
}

/**
 * Hook for deleting media
 */
export function useMediaDelete() {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const deleteMedia = async (mediaId, onSuccess) => {
    setDeleting(true);
    setError(null);

    try {
      const response = await apiRequest(`/media/${mediaId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete media');
      }

      if (onSuccess) {
        onSuccess();
      }

      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setDeleting(false);
    }
  };

  return { deleteMedia, deleting, error };
}

/**
 * Hook for updating media
 */
export function useMediaUpdate() {
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);

  const updateMedia = async (mediaId, updates, onSuccess) => {
    setUpdating(true);
    setError(null);

    try {
      const response = await apiRequest(`/media/${mediaId}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        throw new Error('Failed to update media');
      }

      const mediaRecord = await response.json();

      if (onSuccess) {
        onSuccess(mediaRecord);
      }

      return mediaRecord;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setUpdating(false);
    }
  };

  return { updateMedia, updating, error };
}

// Helper function to get image dimensions
function getImageDimensions(file) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => {
      resolve({ width: null, height: null });
      URL.revokeObjectURL(img.src);
    };
    img.src = URL.createObjectURL(file);
  });
}
